import { Localized } from '@fluent/react';
import { LoadingButton } from '@mui/lab';
import {
    Alert,
    Box,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppId } from '../../../app/appTypes';
import { CancelButton } from '../../../app/components/CancelButton';
import { DocumentNumberTextField } from '../controls/DocumentNumberTextField';
import { StructureTreeSelect } from '../../structure/StructureTreeSelect';
import { CopyButton } from '../../../app/components/CopyButton';

interface CreateDocumentCopyDialogProps {
    open: boolean;
    onClose: () => void;
    documentId: AppId;
    sourceDocumentNo?: string;
    sourceDocumentTitle?: string;
    defaultLanguage?: string;
    defaultStructureId?: AppId | '';
}

export const CreateDocumentCopyDialog = ({
    open,
    onClose,
    documentId,
    sourceDocumentNo,
    sourceDocumentTitle,
    defaultLanguage = 'PL',
    defaultStructureId = '',
}: CreateDocumentCopyDialogProps) => {
    const navigate = useNavigate();

    const [deviceTypeId, setDeviceTypeId] = useState<AppId | ''>(defaultStructureId);
    const [newDocumentNo, setNewDocumentNo] = useState(sourceDocumentNo ?? '');
    const [newDocumentTitle, setNewDocumentTitle] = useState(sourceDocumentTitle ?? '');
    const [language, setLanguage] = useState(defaultLanguage);
    const [copyTeam, setCopyTeam] = useState(true);
    const [validationErrors, setValidationErrors] = useState(false);
    const [copying, setCopying] = useState(false);

    const numberError = validationErrors && newDocumentNo.trim() === '';
    const titleError = validationErrors && newDocumentTitle.trim() === '';
    const structureError = validationErrors && !deviceTypeId;

    const handleClose = () => {
        if (!copying) {
            onClose();
        }
    };

    const handleCopy = async () => {
        setValidationErrors(true);

        if (!deviceTypeId || newDocumentNo.trim() === '' || newDocumentTitle.trim() === '') {
            return;
        }

        setCopying(true);
        try {
            navigate('/urs/create/', {
                state: {
                    defaultUrsId: documentId,
                    copySource: true,
                    prefillDeviceTypeId: deviceTypeId,
                    prefillUrsNumber: newDocumentNo.trim(),
                    prefillUrsTitle: newDocumentTitle.trim(),
                    prefillLanguage: language,
                    copyTeam,
                },
            });
            onClose();
        } finally {
            setCopying(false);
        }
    };

    return (
        <Dialog open={open} fullWidth maxWidth="md">
            <DialogTitle>
                <Localized id="document-create-copy">
                    <span>_Utworz kopie dokumentu (copy)</span>
                </Localized>
            </DialogTitle>

            <DialogContent dividers>
                <Alert severity="warning" variant="outlined">
                    <Localized id="feature-under-development">
                        <span>_Sorry, this feature is under development and will be available soon.</span>
                    </Localized>
                </Alert>
                <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                        <Localized
                            id="document-copy-source-info"
                            vars={{
                                documentNo: sourceDocumentNo ? `"${sourceDocumentNo}"` : '-',
                                documentTitle: sourceDocumentTitle ?? '-',
                            }}
                        >
                            <span>_
                                Utworzysz kopie dokumentu na podstawie: {sourceDocumentNo ? `"${sourceDocumentNo}"` : '-'} - {sourceDocumentTitle ?? '-'}.
                            </span>
                        </Localized>
                    </Typography>
                </Alert>

                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <StructureTreeSelect
                            defaultStructureId={deviceTypeId}
                            onStructureSelected={setDeviceTypeId}
                            canSelectNonLeaf={false}
                        />
                        {structureError && (
                            <Typography variant="caption" color="error">
                                <Localized id="field-is-required"><span>_Pole jest wymagane</span></Localized>
                            </Typography>
                        )}
                    </Grid>

                    <Grid item xs={8}>
                        <DocumentNumberTextField
                            fullWidth={true}
                            required={true}
                            value={newDocumentNo}
                            onChange={(e) => setNewDocumentNo(e.target.value)}
                            label={<Localized id="urs-number"><span>_Numer URS</span></Localized>}
                            documentType={'URS'}
                        />
                        {numberError && (
                            <Typography variant="caption" color="error">
                                <Localized id="field-is-required"><span>_Pole jest wymagane</span></Localized>
                            </Typography>
                        )}
                    </Grid>

                    <Grid item xs={4}>
                        <FormControl fullWidth>
                            <InputLabel id="copy-language-label">
                                <Localized id="document-language"><span>_Jezyk</span></Localized>
                            </InputLabel>
                            <Select
                                labelId="copy-language-label"
                                value={language}
                                onChange={(e) => setLanguage(e.target.value)}
                                label={<Localized id="document-language"><span>_Jezyk</span></Localized>}
                                fullWidth
                            >
                                <MenuItem value="PL">PL</MenuItem>
                                <MenuItem value="EN">EN</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            required
                            fullWidth
                            value={newDocumentTitle}
                            error={titleError}
                            onChange={(e) => setNewDocumentTitle(e.target.value)}
                            label={<Localized id="urs-title"><span>_Tytul</span></Localized>}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Box sx={{ pt: 0.5 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={copyTeam}
                                        onChange={(e) => setCopyTeam(e.target.checked)}
                                    />
                                }
                                label={<Localized id="document-copy-team"><span>_Skopiuj zespol</span></Localized>}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions>
                <CopyButton disabled={true} loading={copying} onClick={handleCopy} />
                <CancelButton loading={copying} onClick={handleClose} />
            </DialogActions>
        </Dialog>
    );
};
